import { useContext, useMemo, useState } from "react";
import { validate } from "email-validator";
import moment from "moment";
import styled from "styled-components";
import { Button, Checkbox, Dialog, InputGroup } from "@blueprintjs/core";

import { refreshUserAuth } from "@api";
import { didUserUseCliLogin, useHasFeature } from "@hooks";
import { UserContext } from "@providers";
import { withToaster } from "@utils";

import { putAcceptedTOC, putUserEmail } from "../../api";

const Container = styled.div`
  padding: 40px 16px 0 40px;
  width: 600px;
  position: relative;
`;

const Header = styled.div`
  font-size: var(--global-font-size-l);
`;

const TopRight = styled.div`
  position: absolute;
  top: 12px;
  right: 8px;
`;

const Checkboxes = styled.div`
  display: grid;
  grid-template-columns: min-content 1fr;
  grid-gap: 8px;
  padding-top: 36px;
`;

const InputDiv = styled.div`
  display: flex;
  gap: 20px;
  padding-top: 12px;
`;

const AlignRight = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 36px;
`;

/**
 *
 * @returns Dialog that is automatically open if we need to collect the user's email
 * This will always pop up when a user signs in for the first time
 * Even if the user closes this dialog, we will collect the first email that is associated
 * with the account they signed in with
 */
export const EmailAndTOCDialog = () => {
  const { user } = useContext(UserContext);
  const [acceptedMonthlyEmails, setAcceptedMonthlyEmails] = useState(true);
  const [acceptedBetaEmails, setAcceptedBetaEmails] = useState(true);
  const [acceptedTOC, setAcceptedTOC] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // manually setting the dialog to closed because the user context doesn't refresh until the user reloads the page
  const [isClosed, setIsClosed] = useState(false);
  const noAvailableEmails =
    user?.available_emails === undefined || user?.available_emails.length === 0;
  const [email, setEmail] = useState(
    noAvailableEmails ? undefined : user.available_emails![0].email
  );

  const needsUserAction =
    user && (!user.email || user.flags?.is_invite_pending === true);

  // there was a bug that caused some users to be able to close the dialog without confirming
  // let's filter for these users and show them the dialog again
  const userCreatedDuringBug =
    !!user &&
    !!user.user_created_at &&
    // these dates are when the bug was live
    moment(user.user_created_at).isBetween("2022-07-04", "2022-08-01");

  const userAlreadyConfirmed =
    user?.flags !== undefined &&
    (user.flags.has_accepted_toc === true ||
      // if they said we can contact them about experiments, we know they did accept
      // because this can't be set to false if they closed the dialog (see onClose)
      user.flags.contact_about_experiments === true);

  const userNeedsToReConfirm = userCreatedDuringBug && !userAlreadyConfirmed;

  const isValidEmail = useMemo(() => !!email && validate(email), [email]);

  const hasNonStandardTOC = useHasFeature("hasNonStandardTOC") !== undefined;

  // valid email entered and TOC agreed to (if necessary)
  const doneButtonEnabled = useMemo(
    () => isValidEmail && (acceptedTOC || hasNonStandardTOC),
    [isValidEmail, acceptedTOC, hasNonStandardTOC]
  );

  const onSubmit = async () => {
    // should never get here
    if (!doneButtonEnabled) return;

    setIsLoading(true);
    try {
      await withToaster(
        async () => {
          const isCliLogin = didUserUseCliLogin();
          await putUserEmail(
            email!,
            acceptedMonthlyEmails,
            acceptedBetaEmails,
            isCliLogin
          );
          await putAcceptedTOC(acceptedTOC);
          await refreshUserAuth();
        },
        (err) => `Could not save email: ${err.body?.error}`
      )();
    } catch {}
    setIsLoading(false);
    setIsClosed(true);
  };

  const onClose = () => {
    // user should not be able to close without entering a valid email
    if (!doneButtonEnabled) return;

    // save user email, default to no email notifications
    const isCliLogin = didUserUseCliLogin();
    putUserEmail(email!, false, false, isCliLogin)
      .then(refreshUserAuth)
      .catch((e) => console.error(e))
      .finally(() => setIsClosed(true))
      .then(() => putAcceptedTOC(acceptedTOC));
  };

  // this dialog shouldn't even show up if there is no logged in user
  if (!user) {
    return <></>;
  }

  return (
    <Dialog isOpen={(needsUserAction || userNeedsToReConfirm) && !isClosed}>
      <Container>
        <Header>
          {hasNonStandardTOC
            ? "Updates and Early Access"
            : "Terms, Updates, and Early Access"}
        </Header>
        {!noAvailableEmails && (
          // user can only close out if we have an email to save
          <TopRight>
            <Button
              disabled={!doneButtonEnabled}
              onClick={onClose}
              minimal
              icon="cross"
            />
          </TopRight>
        )}
        <Checkboxes>
          {!hasNonStandardTOC && (
            <>
              <Checkbox
                checked={acceptedTOC}
                onChange={() => setAcceptedTOC(!acceptedTOC)}
                large
              />
              <div>
                I agree to the{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </a>
              </div>
            </>
          )}
          <Checkbox
            checked={acceptedMonthlyEmails}
            onChange={() => setAcceptedMonthlyEmails(!acceptedMonthlyEmails)}
            large
          />
          <div>Send me monthly updates about new features and changes</div>
          <Checkbox
            checked={acceptedBetaEmails}
            onChange={() => setAcceptedBetaEmails(!acceptedBetaEmails)}
            large
          />
          <div>Get early access to unreleased features and experiments</div>
        </Checkboxes>
        {noAvailableEmails && (
          // if there are no available emails, we need the user to give us one before they continue
          <InputDiv>
            <div>Email</div>
            <InputGroup
              value={email}
              placeholder={"email"}
              onChange={(e) => setEmail(e.target.value)}
            />
          </InputDiv>
        )}
        <AlignRight>
          <Button
            intent="primary"
            loading={isLoading}
            onClick={onSubmit}
            disabled={!doneButtonEnabled}
          >
            Done
          </Button>
        </AlignRight>
      </Container>
    </Dialog>
  );
};
